import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { fmtINR } from "@/lib/api";
import Breadcrumbs from "@/components/layout/Breadcrumbs";
import { Star, MapPin, Clock, Wallet, Globe, Check } from "lucide-react";

export default function DestinationDetail() {
  const { slug } = useParams();
  const [d, setD] = useState(null);
  const [deals, setDeals] = useState([]);
  useEffect(() => {
    api.get(`/destinations/${slug}`).then(({ data }) => setD(data)).catch(() => {});
    api.get("/deals").then(({ data }) => setDeals(data)).catch(() => {});
  }, [slug]);
  if (!d) return <div className="container-page py-20 text-center text-slate-500">Loading…</div>;

  const localDeals = deals.filter((x) => x.destination.toLowerCase().includes(d.name.toLowerCase())).slice(0, 3);

  return (
    <div data-testid={`destination-detail-${slug}`}>
      <div className="relative h-[420px] md:h-[520px] overflow-hidden">
        <img src={d.image} alt={d.name} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="container-page relative z-10 h-full flex flex-col justify-end pb-10 text-white">
          <div className="text-xs font-bold uppercase tracking-widest text-accent mb-3 inline-flex items-center gap-1.5">
            <MapPin size={12} /> {d.country}
          </div>
          <h1 className="font-montserrat font-extrabold text-4xl md:text-6xl tracking-tight">{d.name}</h1>
          <p className="mt-3 text-white/90 max-w-xl text-lg">{d.tagline}</p>
          <div className="mt-5 flex items-center gap-2 text-sm">
            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-white/15 backdrop-blur-sm">
              <Star size={12} className="text-accent fill-accent" /> {d.rating}
            </span>
            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-white/15 backdrop-blur-sm">
              From {fmtINR(d.from_price)}
            </span>
          </div>
        </div>
      </div>

      <div className="container-page py-10">
        <Breadcrumbs items={[{ label: "Destinations", to: "/destinations" }, { label: d.name }]} />

        <div className="grid md:grid-cols-3 gap-8">
          <div className="md:col-span-2">
            <h2 className="font-montserrat font-bold text-2xl text-slate-900 mb-3">About {d.name}</h2>
            <p className="text-slate-700 leading-relaxed">{d.description}</p>

            <h2 className="font-montserrat font-bold text-2xl text-slate-900 mt-8 mb-3">Top experiences</h2>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {d.highlights.map((h) => (
                <li key={h} className="flex items-center gap-2 text-slate-700 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                  <Check size={14} className="text-brand" /> {h}
                </li>
              ))}
            </ul>

            {localDeals.length > 0 && (
              <>
                <h2 className="font-montserrat font-bold text-2xl text-slate-900 mt-8 mb-3">Deals for {d.name}</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {localDeals.map((deal) => (
                    <a key={deal.id} href={deal.affiliate_url} target="_blank" rel="noopener sponsored" className="block bg-white border border-slate-200 rounded-xl p-4 card-hover">
                      <div className="text-xs uppercase tracking-wider text-accent font-bold">{deal.discount_percent}% Off</div>
                      <div className="font-montserrat font-bold text-slate-900">{deal.title}</div>
                      <div className="text-sm text-slate-500 mt-1">{deal.duration} · {fmtINR(deal.new_price)}</div>
                    </a>
                  ))}
                </div>
              </>
            )}
          </div>

          <aside className="space-y-3">
            <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-3">
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Travel essentials</div>
              <div className="text-sm text-slate-700 flex items-center gap-2"><Clock size={14} className="text-brand" /> Best time: {d.best_time}</div>
              <div className="text-sm text-slate-700 flex items-center gap-2"><Wallet size={14} className="text-brand" /> Currency: {d.currency}</div>
              <div className="text-sm text-slate-700 flex items-center gap-2"><Globe size={14} className="text-brand" /> Language: {d.language}</div>
              <div className="text-sm text-slate-700 flex items-center gap-2"><MapPin size={14} className="text-brand" /> Visa: {d.visa_required ? "Required" : "Free / On Arrival"}</div>
            </div>
            <Link to="/contact" data-testid="dest-plan-trip" className="block bg-accent text-white rounded-2xl p-5 text-center card-hover">
              <div className="font-montserrat font-bold">Plan my trip</div>
              <div className="text-sm text-white/85 mt-1">Get a custom quote in 24h</div>
            </Link>
          </aside>
        </div>
      </div>
    </div>
  );
}
